// Helpers of the F&F KPI dashboard. Everything here is presentation: the
// counters, the averages and the evolution percentages all arrive computed from
// the backend (MR !1039) and are never recalculated client-side.

import { DSOC_PROFILE } from "constants/constants";
import { ROLE_ADMIN } from "access-roles/roles";

// --- access -----------------------------------------------------------------

// Who sees the KPI dashboard: the "Directeur de Société" profile, or a
// platform administrator — the exact population the backend accepts
// (kpi.scope.rs: ROLE_ASSOC carried by the Dsoc profile, ROLE_ADMIN listed
// explicitly because the role hierarchy does not make it inherit ROLE_ASSOC).
// One predicate for the menu entry (utils/helper.js) and the route mount
// (pages/manager-kpi), so the two gates cannot drift apart.
export const isFafKpiAllowed = ({ profileId, roles }) =>
  profileId === DSOC_PROFILE || (roles || []).includes(ROLE_ADMIN);

// --- ISO weeks --------------------------------------------------------------

// ISO-8601 week number of a date, Monday-based, week 1 being the one holding
// the first Thursday of the year — the same definition the backend uses.
export const getIsoWeek = (date = new Date()) => {
  const target = new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
  );
  const dayNumber = target.getUTCDay() || 7;
  target.setUTCDate(target.getUTCDate() + 4 - dayNumber);
  const yearStart = new Date(Date.UTC(target.getUTCFullYear(), 0, 1));
  return Math.ceil(((target - yearStart) / 86400000 + 1) / 7);
};

export const getIsoWeekYear = (date = new Date()) => {
  const target = new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
  );
  const dayNumber = target.getUTCDay() || 7;
  target.setUTCDate(target.getUTCDate() + 4 - dayNumber);
  return target.getUTCFullYear();
};

// A year has 53 ISO weeks when it starts on a Thursday, or on a Wednesday of a
// leap year; 52 otherwise.
export const getIsoWeeksInYear = (year) => {
  const firstDay = new Date(Date.UTC(year, 0, 1)).getUTCDay();
  const isLeap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
  return firstDay === 4 || (isLeap && firstDay === 3) ? 53 : 52;
};

// RG-16: the reminder targets the week *preceding* the selected one — and week
// 1 sends us back to the last week of the previous year, which is 52 or 53.
export const getPreviousIsoWeek = ({ week, year }) =>
  week > 1
    ? { week: week - 1, year }
    : { week: getIsoWeeksInYear(year - 1), year: year - 1 };

// Monday of an ISO week, so a week number can be shown as the dates it covers.
export const getIsoWeekStart = ({ week, year }) => {
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const jan4Day = jan4.getUTCDay() || 7;
  const week1Monday = new Date(jan4);
  week1Monday.setUTCDate(jan4.getUTCDate() - jan4Day + 1);
  const monday = new Date(week1Monday);
  monday.setUTCDate(week1Monday.getUTCDate() + (week - 1) * 7);
  return monday;
};

// "25 – 31 août". A week number alone says nothing about the period it covers;
// the range is what a reader actually recognises.
export const formatIsoWeekRange = ({ week, year, locale = "fr-FR" }) => {
  const monday = getIsoWeekStart({ week, year });
  const sunday = new Date(monday);
  sunday.setUTCDate(monday.getUTCDate() + 6);
  const sameMonth = monday.getUTCMonth() === sunday.getUTCMonth();
  const day = (date) =>
    date.toLocaleDateString(locale, { day: "numeric", timeZone: "UTC" });
  const dayMonth = (date) =>
    date.toLocaleDateString(locale, {
      day: "numeric",
      month: "short",
      timeZone: "UTC",
    });
  return sameMonth
    ? `${day(monday)} – ${dayMonth(sunday)}`
    : `${dayMonth(monday)} – ${dayMonth(sunday)}`;
};

// --- formatting -------------------------------------------------------------

// RG-45: the transformation rate always shows one decimal, followed by "%".
export const formatRate = (value) =>
  value === null || value === undefined ? "-" : `${value.toFixed(1)} %`;

// A2: the 3 F&F components each re-derived the Intl.* locale tag from
// react-intl's own `intl.locale` ("fr" | "en") the same way — one accessor.
export const localeForIntl = (intl) =>
  intl?.locale === "en" ? "en-US" : "fr-FR";

// The backend serialises `submitted_at` as a naive UTC datetime ("2026-08-24T
// 23:50:00", no offset) — `new Date()` would otherwise read it as local time
// and shift the calendar day. Append "Z" only when a time component is
// present and no offset already is, so a plain date ("2026-07-27") keeps
// behaving as before. `timeZone` is only there so tests can pin the
// conversion deterministically — callers leave it out to get the browser's
// own zone.
export const formatSubmittedAt = (isoDate, locale = "fr-FR", timeZone) => {
  if (!isoDate) return "";
  const hasTime = isoDate.includes("T");
  const hasOffset = /[zZ]|[+-]\d{2}:?\d{2}$/.test(isoDate);
  const parsed = new Date(hasTime && !hasOffset ? `${isoDate}Z` : isoDate);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toLocaleDateString(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    ...(timeZone ? { timeZone } : {}),
  });
};

// RG-50 prints the podium as medal emoji. EOS types its ranks in figures and
// carries meaning through its own icon set, so the rank is restituted as a
// number and the top three are marked by the `is-podium` class the table
// applies — same information, none of the emoji.
const PODIUM_SIZE = 3;
export const formatRank = (index) => String(index + 1);
export const isPodium = (index) => index < PODIUM_SIZE;

// --- threshold colouring (RG-58) --------------------------------------------

// The three tones are the official charter chip pairs, the same ones the F&F
// wizard uses for its week states: a pastel ground and the saturated ink of the
// SAME family. No new colour is introduced for the dashboard.
export const TONE = {
  GOOD: "good",
  BAD: "bad",
  NEUTRAL: "neutral",
};

export const evolutionTone = (value) => {
  if (value === null || value === undefined || value === 0) {
    return TONE.NEUTRAL;
  }
  return value > 0 ? TONE.GOOD : TONE.BAD;
};

// --- reminder (RG-18) -------------------------------------------------------

// The application never sends anything: it hands a prefilled draft to the
// user's own mail client, and the sending stays entirely in their hands.
export const buildReminderMailto = ({ managers, week }) => {
  const recipients = (managers || [])
    .map((manager) => manager?.email)
    .filter(Boolean)
    .join(",");
  const subject = `Rappel - Formulaire Fast & Furious Semaine ${week}`;
  const body = [
    "Bonjour,",
    "",
    `Vous n'avez pas encore rempli votre formulaire Fast & Furious pour la semaine ${week}.`,
    "",
    "Merci de le compléter dès que possible.",
    "",
    "Cordialement",
  ].join("\n");
  return `mailto:${recipients}?subject=${encodeURIComponent(
    subject
  )}&body=${encodeURIComponent(body)}`;
};

// --- referential ------------------------------------------------------------

// The region referential is read through this single accessor: the branch list
// answers with `name`, and reading the region the same way keeps the dropdowns
// from depending on which of the two spellings that endpoint returns.
export const referentialLabel = (entry) =>
  entry?.name ?? entry?.label ?? entry?.libelle ?? String(entry?.id ?? "");

// RG-11: the branch dropdown only exists while the region is IDF. The
// referential does not hold a region literally named "IDF" — it answers
// "Groupe IDF", "Groupe IDF Digital", "Groupe France"… — so the match is on the
// IDF token, not on the whole label, which an equality test never found.
// Consequence to confirm with the PO: "Groupe IDF Digital" opens the branch
// filter too, since it is an IDF region by that reading.
const IDF_REGION_TOKEN = "IDF";

export const isIdfRegion = (region) =>
  referentialLabel(region)
    .toUpperCase()
    .split(/[^A-Z0-9]+/)
    .includes(IDF_REGION_TOKEN);

// S1: the 6 declaration counters, identical between the récap table
// (FafManagerTable) and the declaration drawer (FafDeclarationDrawer).
//
// `shortId` / `shortLabel` are the abbreviated spellings of the récap header,
// carried only by the four words too long for a narrow list (see the
// « desktop étroit » block of FafKpiDashboard.scss — the uppercase headers are
// what set the matrix's width, and « PROSPECTIONS » alone costs 116px). The
// two remaining labels are short enough as they are and stay whole, which is
// why the field is absent rather than a copy of `label`: FafManagerTable falls
// back to the full label when it is missing.
export const FAF_METRICS = [
  {
    key: "half_day_absences",
    id: "manager_kpi_metric_absences",
    label: "Absences",
    hintId: "manager_kpi_col_absences_hint",
  },
  {
    key: "interview_count",
    id: "manager_kpi_metric_interviews",
    label: "Entretiens",
    shortId: "manager_kpi_metric_interviews_short",
    shortLabel: "Entret.",
  },
  { key: "qualif_count", id: "manager_kpi_metric_qualifs", label: "Qualifs" },
  {
    key: "prospection_count",
    id: "manager_kpi_metric_prospections",
    label: "Prospections",
    shortId: "manager_kpi_metric_prospections_short",
    shortLabel: "Prosp.",
  },
  {
    key: "go_count",
    id: "manager_kpi_metric_starts",
    label: "Démarrages",
    shortId: "manager_kpi_metric_starts_short",
    shortLabel: "Démarr.",
  },
  {
    key: "rea_count",
    id: "manager_kpi_metric_requalifs",
    label: "Requalifs",
    shortId: "manager_kpi_metric_requalifs_short",
    shortLabel: "Requal.",
  },
];

// --- v5.2 : piste de saison, cases d'assiduité, fusion des lignes ---------

export const WEEK_CELL = {
  SUBMITTED: "submitted",
  MISSING: "missing",
  PENDING: "pending",
};

// ISO week holding the 1st of each month of `year`; the month label sits on
// that column of the track. A January 1st that belongs to the previous ISO
// year (e.g. 2027-01-01 is S53 of 2026) is pinned to week 1.
export const getMonthColumns = (year) =>
  Array.from({ length: 12 }, (_, month) => {
    const first = new Date(year, month, 1);
    const week = getIsoWeekYear(first) === year ? getIsoWeek(first) : 1;
    return { month, week };
  });

export const isFutureWeek = ({ week, year, today = new Date() }) => {
  const currentYear = getIsoWeekYear(today);
  const currentWeek = getIsoWeek(today);
  return year > currentYear || (year === currentYear && week > currentWeek);
};

// 0 = no submission, 1..4 = quartile of the non-zero manager counts of the
// year. Quartiles rather than fixed thresholds: the tint is relative to the
// year, the legend says « peu → beaucoup ».
export const computeTrackLevels = (rows) => {
  const levels = new Map();
  const counts = (rows || [])
    .map((row) => Number(row?.manager_count) || 0)
    .filter((count) => count > 0)
    .sort((a, b) => a - b);
  (rows || []).forEach((row) => {
    const count = Number(row?.manager_count) || 0;
    if (count === 0) {
      levels.set(row.week, 0);
      return;
    }
    const rank = counts.findIndex((value) => value >= count);
    levels.set(
      row.week,
      1 + Math.min(3, Math.floor((rank * 4) / counts.length))
    );
  });
  return levels;
};

export const weekCellState = ({
  hasRow,
  week,
  year,
  currentWeek,
  currentYear,
}) => {
  if (hasRow) return WEEK_CELL.SUBMITTED;
  if (week === currentWeek && year === currentYear) return WEEK_CELL.PENDING;
  return WEEK_CELL.MISSING;
};

// The four ISO weeks ending at ({week, year}), oldest first.
export const getWindowWeeks = ({ week, year, size = 4 }) => {
  const weeks = [];
  let cursor = { week, year };
  for (let i = 0; i < size; i += 1) {
    weeks.unshift(cursor);
    cursor = getPreviousIsoWeek(cursor);
  }
  return weeks;
};

// A3: value only — the French/English wording is FafManagerTable's
// FormattedMessage, keyed by `type` (manager_kpi_alert_prosp / _transfo).
const alertLabel = {
  prosp: (row) => ({ type: "prosp", value: row?.prospection_count ?? 0 }),
  transfo: (row) => ({
    type: "transfo",
    value: formatRate(row?.transformation_rate),
  }),
};

// One row per manager. `summary` holds SUBMITTED declarations of the four
// weeks ending at the selected one; `late` holds the managers late on the
// week preceding the selected one.
//
// Since back MR 13088/fix/kpi-spec-conformance every source row carries a
// `manager_id`, so the merge and the alert joins are keyed by id first; the
// name (TRIM(first_name || ' ' || last_name) on the back, identical across
// endpoints) only remains as a fallback for a row that would miss its id.
export const mergeManagerRows = ({
  summary,
  late,
  week,
  year,
  currentWeek = getIsoWeek(),
  currentYear = getIsoWeekYear(),
  insufficient = [],
  transformation = [],
}) => {
  const windowWeeks = getWindowWeeks({ week, year });
  // Two indexes over one list of row objects: the id is the identity, the
  // name only answers for an id-less source row — falling back to the name
  // when the id simply is not indexed yet would re-merge homonyms.
  const rows = [];
  const byId = new Map();
  const byName = new Map();
  const findRow = ({ id, name }) =>
    id !== null && id !== undefined ? byId.get(id) : byName.get(name);
  const indexRow = (row) => {
    rows.push(row);
    if (row.managerId !== null && row.managerId !== undefined) {
      byId.set(row.managerId, row);
    }
    if (!byName.has(row.name)) byName.set(row.name, row);
    return row;
  };

  (summary || []).forEach((declaration) => {
    const name = declaration?.manager_name;
    if (!name) return;
    const row =
      findRow({ id: declaration.manager_id, name }) ??
      indexRow({
        key: String(declaration.manager_id ?? name),
        managerId: declaration.manager_id ?? null,
        name,
        email: null,
        branch: declaration.branch_name ?? null,
        isLate: false,
        declarations: [],
      });
    row.declarations.push(declaration);
  });

  (late || []).forEach((manager) => {
    const name = manager?.name;
    if (!name) return;
    const row = findRow({ id: manager.manager_id, name });
    if (row) {
      row.isLate = true;
      row.email = manager.email ?? null;
    } else {
      indexRow({
        key: String(manager.manager_id ?? name),
        managerId: manager.manager_id ?? null,
        name,
        email: manager.email ?? null,
        branch: null,
        isLate: true,
        declarations: [],
      });
    }
  });

  // id first, name as the fallback: an alert row always carries `manager_id`,
  // but the row it lands on only has one when a declaration was submitted.
  const indexAlerts = (rows) => {
    const byId = new Map();
    const byLabel = new Map();
    (rows || []).forEach((row) => {
      if (row?.manager_id !== null && row?.manager_id !== undefined) {
        byId.set(row.manager_id, row);
      }
      if (row?.manager_name) byLabel.set(row.manager_name, row);
    });
    return (target) =>
      (target.managerId !== null && target.managerId !== undefined
        ? byId.get(target.managerId)
        : null) ?? byLabel.get(target.name);
  };

  const findProsp = indexAlerts(insufficient);
  const findTransfo = indexAlerts(transformation);

  return rows.map((row) => {
    const declarations = [...row.declarations].sort((a, b) => a.week - b.week);
    const current =
      declarations.find((d) => Number(d.week) === Number(week)) || null;
    // A manager already flagged late has missed the deadline: their current
    // week never reads as "pending" (still time to submit) — it reads as
    // missing like the rest of their history, unless a declaration says
    // otherwise.
    const weeks = windowWeeks.map((w) => ({
      ...w,
      state: weekCellState({
        hasRow: declarations.some((d) => Number(d.week) === w.week),
        week: w.week,
        year: w.year,
        currentWeek: row.isLate ? null : currentWeek,
        currentYear: row.isLate ? null : currentYear,
      }),
    }));
    const alerts = [];
    const prosp = findProsp(row);
    const transfo = findTransfo(row);
    if (prosp) alerts.push(alertLabel.prosp(prosp));
    if (transfo) alerts.push(alertLabel.transfo(transfo));
    return {
      ...row,
      declarations,
      current,
      points: current?.points_total ?? null,
      weeks,
      alerts,
    };
  });
};

const byName = (a, b) => String(a.name).localeCompare(String(b.name), "fr");

export const sortManagerRows = (rows) =>
  [...(rows || [])].sort((a, b) => {
    if (a.isLate !== b.isLate) return a.isLate ? -1 : 1;
    if (a.isLate) return byName(a, b);
    const pa = a.points ?? null;
    const pb = b.points ?? null;
    if (pa === null && pb === null) return byName(a, b);
    if (pa === null) return 1;
    if (pb === null) return -1;
    return pb - pa || byName(a, b);
  });
